import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import "./styles/index.css";
import DevelopersWebhooksCreate from "./components/Developers/DevelopersWebhooksCreate";
import { AppLayout } from "./components/ui/app-layout";
import DevelopersBase from "./components/Developers/DevelopersBase";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsProvider } from "./PermissionsProvider";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Tutorial from "./components/Tutorial/Tutorial";
import Events from "./components/Event/Events";
import Applications from "./components/Application/Applications";
import ApplicationOverview from "./components/Application/ApplicationOverview";
import RegisterApplication from "./components/Application/RegisterApplication";
import EditApplication from "./components/Application/EditApplication";
import CloneApplication from "./components/Application/CloneApplication";
import RegisterSentinel from "./components/Sentinel/RegisterSentinel";
import EditSentinel from "./components/Sentinel/EditSentinel";
import ApplicationActionOverview from "./components/Application/ApplicationActionOverview";
import Sandbox from "./components/Sandbox/Sandbox";
import { CompanyService } from "./services/CompanyService";
import { Company } from "./model_gen/company";
import { Button } from "./components/ui/button";
import SessionOverview from "./components/Session/SessionOverview";
import BillingBase from "./components/Billing/BillingBase";
import Intercom from "@intercom/messenger-js-sdk";
import AnnotationOverview from "./components/Annotation/AnnotationOverview";
import AnnotationView from "./components/Annotation/AnnotationView";
import EmailOverview from "./components/Email/EmailOverview";
import RequestOverviewScreen from "./components/Request/RequestOverviewScreen";
import SentinelOverview from "./components/Admin/SentinelOverview";
import EvaluationHome from "./components/Evaluation/EvaluationHome";
import EvaluationNew from "./components/Evaluation/EvaluationNew";
import EvaluationDetail from "./components/Evaluation/EvaluationDetail";
import ValidationRun from "./components/Finetune/Validation/ValidationRunView";
import TestSets from "./components/TestSet/TestSets";
import TestSetDetail from "./components/TestSet/TestSetDetail";
import TestSetNew from "./components/TestSet/TestSetNew";
import TestRunNew from "./components/TestRun/TestRunNew";
import TestRunDetail from "./components/TestRun/TestRunDetail";
import TestSetEdit from "./components/TestSet/TestSetEdit";
import IntentGroupsOverview from "./components/Intent/IntentGroupsOverview";
import IntentGroupOverview from "./components/Intent/IntentGroupOverview";
import Datasets from "./components/Finetune/Compositions/Compositions";
import DatasetNew from "./components/Finetune/Datasets/DatasetNew";
import DatasetDetail from "./components/Finetune/Datasets/DatasetDetail";
import DatasetEdit from "./components/Finetune/Datasets/DatasetEdit";
import CompositionNew from "./components/Finetune/Compositions/CompositionNew";
import CompositionDetailView from "./components/Finetune/Compositions/CompositionDetailView";
import CompositionEdit from "./components/Finetune/Compositions/CompositionEdit";
import NewFinetuneRun from "./components/Finetune/FinetuneRun/NewFinetuneRun";
import FinetuneRuns from "./components/Finetune/FinetuneRun/FinetuneRuns";
import FinetuneRunDetail from "./components/Finetune/FinetuneRun/FinetuneRunDetail";
import Models from "./components/Finetune/Models";
import ValidationOverview from "./components/Finetune/Validation/ValidationOverview";
import IngestData from "./components/Admin/IngestData";
import AugmentationDetail from "./components/Finetune/Datasets/AugmentationDetail";
import Evaluations from "./components/Evaluations/Evaluations";
import ForgeOverview from "./components/Forge/ForgeOverview";
import ConversationTree from "./components/Admin/ConversationTree/components/ConversationTree";
import ConversationTrees from "./components/Admin/ConversationTrees";
import ConversationTreeExecution from "./components/Admin/ConversationTreeExecution";
import AgentOverview from "./components/Forge/AgentOverview";
import AgentIntentManagement from "./components/Forge/AgentIntentManagement";

declare global {
  interface Window {
    heap: any;
  }
}

const App: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const {
    user,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0();
  const domain = "dev-ociu84lf8keizm5i.us.auth0.com";
  const [userMeta, setUserMeta] = useState<any>([]);
  const [affiliated, setAffiliated] = useState<
    { company_name: string; key_value: string }[]
  >([]);
  const [companyId, setCompanyId] = useState<number | null>(() => {
    const savedCompanyId = localStorage.getItem("selectedCompanyId");
    return savedCompanyId ? parseInt(savedCompanyId, 10) : null;
  });

  const loadCompanies = (apiKey: string) => {
    CompanyService.getAffiliatedCompanies(null, apiKey).subscribe(
      (data: any) => {
        setAffiliated(data);
        // If we have a saved company ID, verify it's still valid
        const savedCompanyId = localStorage.getItem("selectedCompanyId");
        if (savedCompanyId) {
          const isValidCompany = data.some(
            (company: Company) => company.id === parseInt(savedCompanyId, 10)
          );
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompanyId");
            setCompanyId(null);
          }
        }
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  useEffect(() => {
    const invitation = searchParams.get("invitation");
    const organization = searchParams.get("organization");
    if (location.pathname === "/login" && invitation && organization) {
      loginWithRedirect({
        authorizationParams: {
          organization,
          invitation,
        },
      });
    }
  }, [location, searchParams, loginWithRedirect]);

  useEffect(() => {
    if (searchParams.get("logout")) {
      console.log("logout");
      searchParams.delete("logout");
      setSearchParams(searchParams);
      logout({ logoutParams: { returnTo: window.location.origin } }).then(
        () => {}
      );
    }
    if (isAuthenticated) {
      if (searchParams.get("logout")) {
        logout({ logoutParams: { returnTo: window.location.origin } }).then(
          () => {
            loginWithRedirect();
          }
        );
      }
      Intercom({
        app_id: "lgh3y6pe",
        user_id: user?.email,
        name: user?.name,
        email: user?.email,
        created_at: user?.createdAt,
      });
      window.heap.identify(user?.email);
      getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${domain}/api/v2/`,
          scope: "openid profile email read:current_user",
        },
      })
        .then((accessToken) => {
          if (!accessToken) {
            console.error("No access token returned");
            return;
          }
          // Use the access token to fetch user profile from the /userinfo endpoint
          fetch(`https://${domain}/api/v2/users/${user?.sub}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          })
            .then((response) => response.json())
            .then((profile) => {
              // Access user_metadata from the profile
              setUserMeta(profile["user_metadata"]);
              loadCompanies(profile["user_metadata"]?.api_key);
              // You can now set this information in your state or use it as needed
            })
            .catch((err) => {
              console.error(
                "Error fetching user profile, redirecting to login",
                err
              );
              logout({
                logoutParams: { returnTo: window.location.origin },
              }).then(() => {
                loginWithRedirect();
              });
            });
        })
        .catch((err) => {
          console.error("Error getting access token", err);
          if (err.error === "consent_required") {
            console.log("consent_required, redirecting to login");
            logout({ logoutParams: { returnTo: window.location.origin } }).then(
              () => {
                loginWithRedirect();
              }
            );
          }
        });
    } else if (!isLoading) {
      if (
        !searchParams.get("error_description") &&
        location.pathname !== "/login"
      ) {
        loginWithRedirect();
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  ]);

  useEffect(() => {
    if (!userMeta || !userMeta.api_key) {
      // logout({logoutParams: {returnTo: window.location.origin }}).then(() => {
      //     loginWithRedirect();
      // });
    } else {
      // console.log("userMeta", userMeta);
    }
  }, [userMeta, logout]);

  // Update companyId when API key changes
  useEffect(() => {
    if (userMeta?.api_key) {
      CompanyService.getCompany(null, userMeta.api_key).subscribe(
        (company: Company) => {
          if (company?.id) {
            setCompanyId(company.id);
            localStorage.setItem("selectedCompanyId", company.id.toString());
          }
        },
        (error: any) => {
          console.error("Error fetching company:", error);
        }
      );
    }
  }, [userMeta?.api_key]);

  const handleRefresh = () => {
    setSearchParams({});
    window.location.reload();
  };
  
  const organizationError = searchParams
    ?.get("error_description")
    ?.includes("organization");
    
  if (searchParams.get("error_description")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-auto text-center space-y-6 p-6">
          <h1 className="text-2xl font-gc-demi-bold text-foreground">
            {organizationError
              ? "Organization not found"
              : "Email Verification"}
          </h1>
          <div className="space-y-4">
            <p className="text-foreground">
              {searchParams.get("error_description")}
            </p>
            <p className="text-muted-foreground text-sm">
              {organizationError
                ? "Please contact support@trymaitai.ai if you believe this is an error."
                : "Click continue after you've verified your email."}
            </p>
          </div>
          {organizationError ? (
            <Button asChild>
              <a href="mailto:support@trymaitai.ai">Contact Support</a>
            </Button>
          ) : (
            <Button onClick={handleRefresh}>Continue</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {userMeta && userMeta.api_key !== undefined ? (
        <PermissionsProvider apiKey={userMeta.api_key} companyId={companyId}>
          <Routes>
            <Route
              path="/"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Tutorial />
                </AppLayout>
              }
            />
            <Route
              path="/applications"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Applications />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId"
              element={
                <AppLayout>
                  <ApplicationOverview />
                </AppLayout>
              }
            />
            <Route
              path="/application/new"
              element={
                <AppLayout>
                  <RegisterApplication />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/edit"
              element={
                <AppLayout>
                  <EditApplication />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/clone"
              element={
                <AppLayout>
                  <CloneApplication />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/sentinel/new"
              element={
                <AppLayout>
                  <RegisterSentinel />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/sentinel/:sentinelId"
              element={
                <AppLayout>
                  <EditSentinel />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/intent/:actionId"
              element={
                <AppLayout>
                  <ApplicationActionOverview />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/agents"
              element={
                <AppLayout>
                  <AgentOverview />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/agent/:agentId/intents"
              element={
                <AppLayout>
                  <AgentIntentManagement />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets"
              element={
                <AppLayout>
                  <TestSets />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets/:testSetId"
              element={
                <AppLayout>
                  <TestSetDetail />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets/new"
              element={
                <AppLayout>
                  <TestSetNew />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets/:testSetId/edit"
              element={
                <AppLayout>
                  <TestSetEdit />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets/:testSetId/runs/new"
              element={
                <AppLayout>
                  <TestRunNew />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/testsets/:testSetId/runs/:testRunId"
              element={
                <AppLayout>
                  <TestRunDetail />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId/session/:sessionId"
              element={
                <AppLayout>
                  <SessionOverview />
                </AppLayout>
              }
            />
            <Route
              path="/settings"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <DevelopersBase />
                </AppLayout>
              }
            />
            <Route
              path="/settings/webhooks/create"
              element={<DevelopersWebhooksCreate />}
            />
            <Route
              path="/sandbox"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Sandbox />
                </AppLayout>
              }
            />
            <Route
              path="/events"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Events />
                </AppLayout>
              }
            />
            <Route
              path="/billing"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <BillingBase />
                </AppLayout>
              }
            />
            <Route
              path="/admin/annotation"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <AnnotationOverview />
                </AppLayout>
              }
            />
            <Route
              path="/admin/annotation/company/:companyId/application/:appId/intent/:intentId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <AnnotationView />
                </AppLayout>
              }
            />
            <Route
              path="/admin/email"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <EmailOverview />
                </AppLayout>
              }
            />
            <Route
              path="/application/:applicationId/session/:sessionId/request/:requestId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <RequestOverviewScreen />
                </AppLayout>
              }
            />
            <Route
              path="/admin/sentinel"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <SentinelOverview />
                </AppLayout>
              }
            />
            <Route
              path="/admin/evaluations"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Evaluations />
                </AppLayout>
              }
            />
            <Route
              path="/admin/conversation-trees"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ConversationTrees />
                </AppLayout>
              }
            />
            <Route
              path="/admin/conversation-tree"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ConversationTree />
                </AppLayout>
              }
            />
            <Route
              path="/admin/conversation-trees/:treeId/execute"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ConversationTreeExecution />
                </AppLayout>
              }
            />
            <Route
              path="/admin/ingest-data"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <IngestData />
                </AppLayout>
              }
            />
            <Route
              path="/evaluation"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <EvaluationHome />
                </AppLayout>
              }
            />
            <Route
              path="/evaluation/new"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <EvaluationNew />
                </AppLayout>
              }
            />
            <Route
              path="/evaluation/:evaluationId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <EvaluationDetail />
                </AppLayout>
              }
            />
            <Route
              path="/build/compositions"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Datasets />
                </AppLayout>
              }
            />
            <Route
              path="/build/compositions/new"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <CompositionNew />
                </AppLayout>
              }
            />
            <Route
              path="/build/compositions/:id"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <CompositionDetailView />
                </AppLayout>
              }
            />
            <Route
              path="/build/compositions/:id/edit"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <CompositionEdit />
                </AppLayout>
              }
            />
            <Route
              path="/build/datasets/new"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <DatasetNew />
                </AppLayout>
              }
            />
            <Route
              path="/build/datasets/:id"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <DatasetDetail />
                </AppLayout>
              }
            />
            <Route
              path="/build/datasets/:id/edit"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <DatasetEdit />
                </AppLayout>
              }
            />
            <Route
              path="/build/datasets/:id/augmentations/:augmentationId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <AugmentationDetail />
                </AppLayout>
              }
            />
            <Route
              path="/build/finetuning"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <FinetuneRuns />
                </AppLayout>
              }
            />
            <Route
              path="/build/finetuning/:finetuneRunId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <FinetuneRunDetail />
                </AppLayout>
              }
            />
            <Route
              path="/build/finetuning/new"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <NewFinetuneRun />
                </AppLayout>
              }
            />
            <Route
              path="/build/finetuning/:finetuneRunId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <FinetuneRunDetail />
                </AppLayout>
              }
            />
            <Route
              path="/build/models"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Models />
                </AppLayout>
              }
            />
            <Route
              path="/build/forge"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ForgeOverview />
                </AppLayout>
              }
            />
            <Route
              path="/admin/validation/overview"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ValidationOverview />
                </AppLayout>
              }
            />
            <Route
              path="/build/finetuning/:finetuneRunId/validation/:validationRunId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <ValidationRun />
                </AppLayout>
              }
            />
            <Route
              path="/test/test-sets"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <TestSets />
                </AppLayout>
              }
            />
            <Route
              path="/test/test-sets/new"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <TestSetNew />
                </AppLayout>
              }
            />
            <Route
              path="/test/evaluations"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <Evaluations />
                </AppLayout>
              }
            />
            <Route
              path="/intent-groups"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <IntentGroupsOverview />
                </AppLayout>
              }
            />
            <Route
              path="/intent-group/:intentGroupId"
              element={
                <AppLayout companies={affiliated} setMeta={setUserMeta}>
                  <IntentGroupOverview />
                </AppLayout>
              }
            />
          </Routes>
        </PermissionsProvider>
      ) : null}
    </>
  );
};

export default () => (
  <ThemeProvider>
    <Router>
      <App />
      <Toaster />
    </Router>
  </ThemeProvider>
);
